import React, { useEffect, useState, useRef } from "react";
import { ymd, xlsYmd } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, MagnifyingGlass, User, FileArrowUp, Download, Key } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatIDR, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";

const POSITIONS = ["Guru", "Kepala Sekolah", "Staff", "Admin", "TU", "Pustakawan", "Cleaning Service", "Security", "Lainnya"];

export default function Employees() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [q, setQ] = useState("");
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const fileInput = useRef();
  const canManage = ["superadmin","admin","hr"].includes(user?.role);

  const load = async () => {
    const [e, l] = await Promise.all([
      api.get("/employees" + (filterLembaga !== "all" ? `?lembaga_id=${filterLembaga}` : "")),
      api.get("/lembaga"),
    ]);
    setItems(e.data); setLembagas(l.data);
  };
  useEffect(() => { load(); }, [filterLembaga]);

  const lMap = Object.fromEntries(lembagas.map((l) => [l.id, l]));

  const openNew = () => {
    setEditing(null);
    setForm({ employee_id: `EMP-${Date.now().toString().slice(-6)}`, name: "", email: "", phone: "", lembaga_id: "", position: "Guru", department: "", join_date: ymd(), status: "aktif", salary_base: 0, tunjangan: 0 });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, lembaga_id: it.lembaga_id || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, salary_base: Number(form.salary_base || 0), tunjangan: Number(form.tunjangan || 0), lembaga_id: form.lembaga_id || null };
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else {
        const { data } = await api.post("/employees", payload);
        if (data.default_password) {
          alert(`Karyawan berhasil dibuat.\n\nAkun login:\nEmail: ${data.login_email}\nPassword awal: ${data.default_password}\n\nMinta karyawan mengganti password setelah login pertama.`);
        }
      }
      toast.success(editing ? "Data karyawan diperbarui" : "Karyawan + akun login dibuat");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus karyawan (beserta akun login-nya)?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };

  const resetPassword = async (emp) => {
    if (!confirm(`Reset password untuk ${emp.name}?`)) return;
    try {
      const { data } = await api.post(`/employees/${emp.id}/reset-password`);
      alert(`Password ${emp.name} direset menjadi: ${data.new_password}\n\nSampaikan password baru ini kepada karyawan.`);
      toast.success("Password direset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadTemplate = () => {
    const headers = ["employee_id", "name", "email", "phone", "position", "department", "join_date", "salary_base", "tunjangan", "nik", "education", "address"];
    const example = ["EMP-100001", "Contoh Guru", "guru@example.com", "081234567890", "Guru", "Kurikulum", "2024-07-01", 3000000, 500000, "3201...", "S1 Pendidikan", "Alamat"];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    XLSX.utils.book_append_sheet(wb, ws, "Karyawan");
    XLSX.writeFile(wb, "template-karyawan.xlsx");
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      const rows = data.filter((d) => d.employee_id && d.name).map((d) => ({
        employee_id: String(d.employee_id),
        name: String(d.name),
        email: d.email ? String(d.email) : null,
        phone: d.phone ? String(d.phone) : null,
        position: String(d.position || "Guru"),
        department: d.department ? String(d.department) : null,
        join_date: d.join_date instanceof Date ? xlsYmd(d.join_date) : (d.join_date ? String(d.join_date) : null),
        status: "aktif",
        salary_base: Number(d.salary_base || 0),
        tunjangan: Number(d.tunjangan || 0),
        nik: d.nik ? String(d.nik) : null,
        education: d.education ? String(d.education) : null,
        address: d.address ? String(d.address) : null,
      }));
      const { data: res } = await api.post("/employees/bulk", rows);
      toast.success(`${res.inserted} karyawan diimport (+ akun login otomatis) dari ${res.total} baris`);
      if (res.errors?.length) alert(`${res.errors.length} gagal:\n` + res.errors.slice(0,10).map(e=>`${e.employee_id}: ${e.error}`).join("\n"));
      load();
    } catch (e) { toast.error("Gagal: " + (e.message || "")); }
  };

  const filtered = items.filter((i) => !q || i.name?.toLowerCase().includes(q.toLowerCase()) || i.employee_id?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Human Resource</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Data Karyawan</h1>
          <p className="text-gray-600 text-sm mt-1">Setiap karyawan otomatis mendapat akun login. Password awal = ID Karyawan.</p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid="employee-template-button"><Download size={16} className="mr-1.5" />Template</Button>
            <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e)=>e.target.files?.[0] && importFile(e.target.files[0])} data-testid="employee-import-file" />
            <Button variant="outline" onClick={()=>fileInput.current?.click()} data-testid="employee-import-button"><FileArrowUp size={16} className="mr-1.5" />Import Excel</Button>
            <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="employee-add-button"><Plus size={16} className="mr-1.5" />Tambah Karyawan</Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari nama atau ID..." className="pl-9" data-testid="employee-search" />
          </div>
          <Select value={filterLembaga} onValueChange={setFilterLembaga}>
            <SelectTrigger className="w-full sm:w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Lembaga</SelectItem>
              {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-gray-500 font-mono self-center">{filtered.length} karyawan</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-4 py-3 font-semibold">ID</th><th className="px-4 py-3 font-semibold">Nama</th><th className="px-4 py-3 font-semibold">Jabatan</th><th className="px-4 py-3 font-semibold">Lembaga</th><th className="px-4 py-3 font-semibold">Bergabung</th><th className="px-4 py-3 font-semibold text-right">Gaji Pokok</th><th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
            </thead>
            <tbody>
              {filtered.map((emp) => (
                <tr key={emp.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{emp.employee_id}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[#0B8D95]/10 text-[#0B8D95] flex items-center justify-center font-bold text-xs">{emp.name?.[0]}</div>
                      <div>
                        <div className="font-semibold text-gray-900">{emp.name}</div>
                        <div className="text-xs text-gray-500">{emp.email || emp.phone || ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">{emp.position}</td>
                  <td className="px-4 py-2.5 text-xs">{lMap[emp.lembaga_id]?.name || "-"}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{formatDate(emp.join_date)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{formatIDR(emp.salary_base)}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${emp.status==="aktif"?"bg-[#CCFBF1] text-[#0B8D95]":"bg-gray-100 text-gray-600"}`}>{emp.status}</span></td>
                  <td className="px-4 py-2.5 text-right">
                    {canManage && <>
                      <button onClick={()=>resetPassword(emp)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Reset Password" data-testid={`employee-reset-${emp.id}`}><Key size={14} /></button>
                      <button onClick={()=>openEdit(emp)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`employee-edit-${emp.id}`}><PencilSimple size={14} /></button>
                      <button onClick={()=>remove(emp.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`employee-delete-${emp.id}`}><Trash size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">Belum ada karyawan</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-2xl" data-testid="employee-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Karyawan" : "Tambah Karyawan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>ID Karyawan *</Label><Input value={form.employee_id || ""} onChange={(e)=>setForm({...form, employee_id: e.target.value})} data-testid="employee-id-input" /></div>
            <div><Label>Nama Lengkap *</Label><Input value={form.name || ""} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="employee-name-input" /></div>
            <div><Label>Email (opsional — dipakai untuk login)</Label><Input value={form.email || ""} onChange={(e)=>setForm({...form, email: e.target.value})} placeholder="jika kosong, pakai ID" /></div>
            <div><Label>No. HP</Label><Input value={form.phone || ""} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Jabatan *</Label>
              <Select value={form.position} onValueChange={(v)=>setForm({...form, position: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{POSITIONS.map((p)=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Lembaga</Label>
              <Select value={form.lembaga_id || "none"} onValueChange={(v)=>setForm({...form, lembaga_id: v === "none" ? "" : v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">Yayasan (Umum)</SelectItem>{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Departemen</Label><Input value={form.department || ""} onChange={(e)=>setForm({...form, department: e.target.value})} /></div>
            <div><Label>Tanggal Bergabung</Label><Input type="date" value={form.join_date || ""} onChange={(e)=>setForm({...form, join_date: e.target.value})} /></div>
            <div><Label>Gaji Pokok</Label><Input type="number" value={form.salary_base || 0} onChange={(e)=>setForm({...form, salary_base: e.target.value})} /></div>
            <div><Label>Tunjangan</Label><Input type="number" value={form.tunjangan || 0} onChange={(e)=>setForm({...form, tunjangan: e.target.value})} /></div>
            <div><Label>NIK</Label><Input value={form.nik || ""} onChange={(e)=>setForm({...form, nik: e.target.value})} /></div>
            <div><Label>Pendidikan</Label><Input value={form.education || ""} onChange={(e)=>setForm({...form, education: e.target.value})} /></div>
            <div><Label>Status</Label>
              <Select value={form.status || "aktif"} onValueChange={(v)=>setForm({...form, status: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="cuti">Cuti</SelectItem><SelectItem value="resign">Resign</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Alamat</Label><Input value={form.address || ""} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="employee-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

